import { Link } from "react-router-dom";
import {
  Card,
  OutcomeCard,
  OUTCOMES,
  PriceTag,
  Shell,
  LinkButton,
  delay,
  EquationCard,
  EvidenceBlock,
  ReadingScene,
  Page,
  Magnifier,
  Star,
  Tick,
  OPERATOR_COMPANY,
  OPERATOR_ADDRESS,
  OPERATOR_UK_REP,
  OPERATOR_EMAIL,
} from "../../ui";
import { Footer } from "../marketing/Footer";
import library from "../../lib/library-catalogue.json";
import { PartnerStrip } from "../../ui/PartnerStrip";
import {
  MONTHLY, MONTHLY_TERMS, PACKS, VAT_LINE, cheapestPack, perPageOf,
} from "../../lib/plans";
import { useEntitlement } from "../../lib/entitlement";
import { sampleLine } from "./sample";
import type { Entitlement } from "../../lib/types";

/* What we are, said against what a parent has probably already tried. No
 * product is named and none needs to be: the three sentences are the whole
 * difference, and each one is a thing this product does rather than a thing
 * somebody else does badly. */
/* The cheapest per-page rate a parent can actually buy today.
 *
 * The cheapest product outright is the monthly plan at 47p, and the monthly
 * plan is not on sale: its terms are not settled and no screen offers a way to
 * start one. Quoting a rate off a product nobody can buy is the same defect as
 * promising its terms, so the front page quotes the cheapest pack instead and
 * says which pack it is. Computed in `plans.ts` rather than typed, so a price
 * edited there does not leave a stale number here. */
const BIGGEST_PACK = cheapestPack();

const eyes = [
  { art: <Page size={40} />, say: "Less time checking every answer.",
    line: "Photograph the page. See what is right, what needs attention and what we could not check, together in one report." },
  { art: <Magnifier size={40} />, say: "Know how to help, even when the maths feels unfamiliar.",
    line: "Plain-English explanations and worked steps help you talk through the tricky part with your child." },
  { art: <Tick size={40} />, say: "Find the next useful practice without the search.",
    line: "Follow the topic from their homework to relevant lessons, worksheets and practice resources." },
];

const steps = [
  { art: <Page size={40} />, title: "Snap the homework", body: "Take or upload a photo of work they have already done. No typing out every question." },
  { art: <Magnifier size={40} />, title: "Understand the sticking point", body: "See the answers we can check, the mistakes we can explain and the working behind them." },
  { art: <Star size={40} />, title: "Make the next few minutes count", body: "Pick one thing to practise together, with relevant material ready to open." },
];

/** The free start, read off the service rather than typed here.
 *
 *  A fresh entitlement carries the pages the service gives away, and that
 *  number is the operator's to change without a deploy of this screen. Two
 *  answers get no number at all: a request that did not land, and a service
 *  running on an allowance rather than on credits, where "granted" is not a
 *  count of free pages and quoting it would be a made-up number. */
export function freeStartLine(ent: Entitlement | null): string {
  const granted = ent?.mode === "credits" ? ent.credits?.granted ?? 0 : 0;
  if (granted < 1) return "Start free";
  return granted === 1
    ? "Your first page is free"
    : `Your first ${granted} pages are free`;
}

export function Landing() {
  /* No learner id: this is the device asking what a page would cost before
   * there is a child to ask about, which is the one entitlement call the
   * service answers with no header. */
  const ent = useEntitlement();
  const sample = sampleLine(ent);

  return (
    <Shell chrome="marketing">
      <div className="paper">
        <section className="page hero">
          <div className="hero-copy">
            <h1 className="anim-rise">
              Homework help.
              <span className="line2">
                <span className="brushed">
                  Less guesswork
                  <svg className="brush" viewBox="0 0 300 16" fill="none" preserveAspectRatio="none" aria-hidden="true">
                    <path
                      d="M6 10.5c58-6 176-8 288-3.5"
                      stroke="var(--marigold)"
                      strokeWidth="10"
                      strokeLinecap="round"
                      className="draw"
                      style={{ ["--len" as string]: 300, ["--delay" as string]: "420ms", ["--draw-dur" as string]: "760ms" }}
                    />
                  </svg>
                </span>
                {" "}for busy parents.
              </span>
            </h1>
            <p className="hero-lead anim-rise" style={delay(90)}>
              Turn a photo of maths homework into a clear plan to help your child. Understand the tricky bits,
              find useful practice and get on with your evening.
            </p>
            <div className="hero-actions anim-rise" style={delay(180)}>
              <LinkButton to="/capture" tone="primary" breathe>
                Photograph a page
              </LinkButton>
              <LinkButton to="/sample" tone="secondary">
                Try a sample report
              </LinkButton>
            </div>
            <p className="hero-price">
              One credit marks one page, from {perPageOf(BIGGEST_PACK)} in the pack of{" "}
              {BIGGEST_PACK.pages}.
            </p>
            <p className="hero-cost">
              A page we cannot mark is free, and credits never expire.
            </p>
          </div>

          <div className="hero-panel">
            <EquationCard className="eq-a" tilt={-7} delay={0} tone="marigold">
              7 × 8 = 56
            </EquationCard>
            <EquationCard className="eq-b" tilt={5} delay={900} tone="sage">
              3/4 of 48 = 36
            </EquationCard>
            <ReadingScene className="hero-scene" />
          </div>
        </section>

        <section className="page section" aria-labelledby="repeat-head">
          <div className="section-head">
            <h2 id="repeat-head">From “I’m stuck” to “let’s try this”</h2>
            <p className="lead">
              See what needs attention and how to explain it. Your saved reports help you spot when the same mistake comes back.
            </p>
          </div>
          <Card tone="content" className="stack repeat">
            <p>
              A small detail can explain a confusing answer. Here, the pupil rounded to a whole number instead of one significant figure.
            </p>
            <EvidenceBlock wrote="1.000" answer="0.6" />
            <p className="small">
              The report puts the written answer beside the expected answer, then connects the topic to a useful next step.
            </p>
          </Card>
        </section>

        <section className="page section" aria-labelledby="eyes-head">
          <div className="section-head">
            <h2 id="eyes-head">You bring the homework. We help with the next step.</h2>
          </div>
          <ul className="eyes stagger">
            {eyes.map((point) => (
              <li className="eye" key={point.say}>
                <span className="eye-art" aria-hidden="true">
                  {point.art}
                </span>
                <div>
                  <p className="eye-say">{point.say}</p>
                  <p className="eye-line">{point.line}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="page section" aria-labelledby="steps-head">

          <div className="section-head">
            <h2 id="steps-head">A photo. A clear explanation. A way forward.</h2>
            <p className="lead">Built for the few minutes you have between everything else.</p>
          </div>
          <ol className="steps stagger">
            {steps.map((step, i) => (
              <li className="step" key={step.title}>
                <span className="step-num" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                  <div className="step-art" aria-hidden="true">
                    {step.art}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="page section" aria-labelledby="outcomes-head">
          <details className="landing-details"><summary>What if a page cannot be marked?</summary>
          <div className="section-head">
            <h2 id="outcomes-head">What you get back, every time</h2>
            <p className="lead">
              We always tell you what happened to a page. There are five ways it can turn out and three of them
              cost nothing: only a page we mark costs a credit.
            </p>
          </div>
          <div className="outcomes stagger">
            {OUTCOMES.map((outcome) => (
              <OutcomeCard key={outcome} outcome={outcome} />
            ))}
          </div>
          </details>
        </section>



        {/* Coverage, as proof rather than promise. Every figure here is read
            from `library.england`, which `scripts/build-public-library.py`
            counts out of the reviewed database at build time, so the page
            cannot outlive the data. Reviewed by Codex on 7 September 2026,
            which refused an earlier draft: it said "a person read each page",
            and our reviews are model editorial reviews. The disclosure below
            is the wording that review asked for. Do not soften it. */}
        <section className="page section" aria-labelledby="library-head">
          <div className="privacy-panel library-panel">
            <h2 id="library-head">Matched to your child’s exam board</h2>
            <p className="lead">
              Built for children at school in England, primary to GCSE. England’s four
              GCSE boards each set out what the course covers, point by point. We have
              worked through all four and linked practice to{" "}
              {library.england.boardRowsWithPractice} of their {library.england.boardRows} points.
            </p>
            <div className="library-stats">
              <div>
                <strong>{library.england.boardRowsWithPractice} of {library.england.boardRows}</strong>
                <span>points across AQA, Edexcel, Eduqas and OCR with practice linked</span>
              </div>
              <div>
                <strong>{library.england.codesWithPublisher} of {library.england.codes}</strong>
                <span>topics the four boards share, each with a worksheet from a named publisher</span>
              </div>
              <div>
                <strong>{Math.floor(library.mathsLessons / 100) * 100}+</strong>
                <span>maths lessons behind them, primary to GCSE</span>
              </div>
            </div>
            <p>
              The practice sits on other people’s websites, mostly Oak National Academy
              and Corbettmaths. We link to the page, name the publisher, and say which
              questions match the point your child is stuck on. An AI model reviewed
              every selection and worked through its answers again before it was listed.
              That is our own check, not an exam board endorsement.
            </p>
            <p className="small"><Link to="/sources">Explore our sources and licences</Link></p>
          </div>
        </section>

        <section className="page section" aria-labelledby="packs-head">
          <div className="section-head">
            <h2 id="packs-head">{freeStartLine(ent)}</h2>
            <p className="lead">
              From {perPageOf(BIGGEST_PACK)} after that. Practice is always free, and nothing renews
              on its own.
            </p>
            {sample ? <p className="lead">{sample}</p> : null}
          </div>
          <div className="packs stagger">
            {PACKS.map((pack) => (
              <Card key={pack.id} className="pack">
                <span className="pack-size">{pack.pages} pages</span>
                <p>{pack.note}</p>
                <div className="pack-foot">
                  <PriceTag pence={pack.pence} pages={pack.pages} note="one off" />
                </div>
              </Card>
            ))}
            <Card tone="surface" className="pack">
              <span className="pack-size">Monthly</span>
              <p>{MONTHLY.pages} pages each month. {MONTHLY_TERMS}</p>
              <div className="pack-foot">
                <PriceTag pence={MONTHLY.pence} pages={MONTHLY.pages} note="a month" />
              </div>
            </Card>
          </div>
          <p className="small" style={{ marginTop: "var(--s-5)" }}>
            Less than a pound a page, and nothing at all for a page we cannot mark. {VAT_LINE} There is no way
            to pay yet, so nothing here has been bought. <Link to="/pricing">See what is free</Link>
          </p>
        </section>

        <section className="page section">
          <Card tone="action" className="stack">
            <h2>Make tonight’s homework easier to help with</h2>
            <p style={{ color: "#E3E8EF" }}>
              Start with one page. Open the report on your own or work through it together, with an explanation and a next step close at hand.
            </p>
            <div style={{ maxWidth: "360px", marginTop: "var(--s-4)" }}>
              <LinkButton to="/capture" tone="secondary">
                Photograph a page
              </LinkButton>
            </div>
          </Card>
        </section>

        {/* Agent V5-C, wave 2. The host's partner list, live hosts only. */}
        <PartnerStrip />

        <Footer
          company={OPERATOR_COMPANY}
          address={OPERATOR_ADDRESS}
          rep={OPERATOR_UK_REP}
          email={OPERATOR_EMAIL}
        />
      </div>
    </Shell>
  );
}
